import { apiService } from './api';
import { ApiResponse } from '../types';
import { BookingDetail, ContactMessageSummary } from './shareLinkService';

/**
 * Consolidated Communication surface (cross-batch bookings + contact messages), parameterised by
 * portal exactly like `makeShareLinkManageService`:
 *  - internal → `api/communication/*` (roles + the revokable `communication` menu capability)
 *  - partner  → `api/partner/v1/communication/*` (partner-scoped server-side)
 * Route suffixes are identical on both surfaces by design. Per-item ACTIONS are not here — reuse
 * `makeShareLinkManageService(portal)` (confirm/decline/cancel/no-show, read/responded are batch-free).
 */

export type CommunicationPortal = 'internal' | 'partner';

const BASE_PATHS: Record<CommunicationPortal, string> = {
  internal: '/communication',
  partner: '/partner/v1/communication',
};

export interface CommunicationListParams {
  pageNumber: number;
  pageSize: number;
  status?: string;
  search?: string;
  fromUtc?: string;
  toUtc?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface BookingListResponse {
  items: BookingDetail[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface ContactMessageListResponse {
  items: ContactMessageSummary[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CommunicationCounts {
  requestedBookings: number;
  newMessages: number;
  /**
   * Sample-intake entries with status `New`. INTERNAL portal only — the partner
   * counts endpoint omits it, so it is optional to keep the partner path typechecking.
   */
  newIntake?: number;
  /**
   * Pending farmer-file uploads awaiting staff linkage. INTERNAL portal only — the
   * partner counts endpoint omits it, so it is optional to keep the partner path typechecking.
   */
  newFiles?: number;
  /**
   * Verified-but-unreviewed self-registered External users. INTERNAL portal only, and
   * composed only for SuperAdmin/Administrator callers (null/absent for Agronomist) — optional.
   */
  newSignups?: number;
}

/** One playable recording: a short-lived presigned URL plus size/duration for the UI. */
export interface MeetRecordingView {
  url?: string | null;
  sizeBytes?: number | null;
  durationSeconds?: number | null;
  startUtc?: string | null;
}

/** A booking's ingested Meet artifacts (transcript + Gemini notes + recordings). */
export interface MeetArtifactView {
  ingestState: string;
  transcriptText?: string | null;
  participants: string[];
  recordings: MeetRecordingView[];
  purged: boolean;
}

const toQuery = (params: CommunicationListParams): string => {
  const query = new URLSearchParams();
  query.append('pageNumber', String(params.pageNumber));
  query.append('pageSize', String(params.pageSize));
  if (params.status) query.append('status', params.status);
  if (params.search) query.append('search', params.search);
  if (params.fromUtc) query.append('fromUtc', params.fromUtc);
  if (params.toUtc) query.append('toUtc', params.toUtc);
  if (params.sortBy) query.append('sortBy', params.sortBy);
  if (params.sortOrder) query.append('sortOrder', params.sortOrder);
  return query.toString();
};

export interface CommunicationService {
  listBookings(params: CommunicationListParams): Promise<ApiResponse<BookingListResponse>>;
  listMessages(params: CommunicationListParams): Promise<ApiResponse<ContactMessageListResponse>>;
  getCounts(): Promise<ApiResponse<CommunicationCounts>>;
  getBookingArtifacts(publicId: string): Promise<ApiResponse<MeetArtifactView>>;
}

export const makeCommunicationService = (portal: CommunicationPortal): CommunicationService => {
  const base = BASE_PATHS[portal];
  return {
    listBookings: (params) =>
      apiService.get<BookingListResponse>(`${base}/bookings?${toQuery(params)}`),
    listMessages: (params) =>
      apiService.get<ContactMessageListResponse>(`${base}/messages?${toQuery(params)}`),
    getCounts: () => apiService.get<CommunicationCounts>(`${base}/counts`),
    getBookingArtifacts: (publicId) =>
      apiService.get<MeetArtifactView>(`${base}/bookings/${publicId}/artifacts`),
  };
};
